<script setup>
import { onMounted, provide } from 'vue';
import { PROS_QUICK_VIEW_TAGGING, PRODUCT_POOL_QUICK_VIEW_TAGGING } from '@nuxt-common/pros-collection/app/constants/features/quickShop';
import { loadTagLibrary } from '@nuxt-common/util/app/utils/tagManager';
import { useProductQVStore } from '@nuxt-common/product-quickview/app/stores/useProductQVStore';
import useQuickShopProvider from '@nuxt-common/product-quickview/app/composables/useQuickShopProvider';
import useExperimentIds from '@/composables/useExperimentIds';
import useSiteMonGAMTags from '@/composables/useSitemonGAMTags';
import { useAppError } from '@/composables/useAppError';
import ExperimentationTags from '@vue-util/experimentation/ExperimentationTags';
import DiscoveryModalOverlay from '@/components/DiscoveryModalOverlay';
import { useDiscoHead } from '@/composables/useDiscoHead';
import useKillSwitches from '@/composables/useKillSwitches';

const { sitemonGAMTagsInSSREnabled, isRoktSDKPlusEnabled } = useKillSwitches();
const productQVStore = useProductQVStore();

const config = useRuntimeConfig();
const brand = import.meta.env.VITE_ENTRY_POINT;
const isClient = import.meta.client;
const tagMngrEnv = config.public.tagMngrEnv;

const { initSiteMonGAMTags } = useSiteMonGAMTags();
if (sitemonGAMTagsInSSREnabled) {
  if (isClient) {
    initSiteMonGAMTags();
  }
} else {
  initSiteMonGAMTags();
}

const { experimentIds } = useExperimentIds();
provide('experimentIds', experimentIds.value.toString() || '');

const { listenErrorCaptured } = useAppError();
listenErrorCaptured();

useDiscoHead();

// We expose toggle method from product-quickview to enable quickshop feature
// for PROS panels on all discovery-ui pages
// carousel-thumbnail holds button view and injects this method from page level
// button visibility is controlled by pros-collection by 2716-21 experiment id
// and a list of eligible zones
useQuickShopProvider({
  // handler for PROS product quickview click
  onOpen: (payload) => {
    const { productId, colorId, slideoutTitle = 'Quick View', isProductPoolCarousel = false } = payload;

    if (!productId) {
      console.warn('Cannot open Quick View: productId is missing.');
      return;
    }

    // set PROS/PRODUCT_POOL quickview tagging
    productQVStore.setTaggingData(isProductPoolCarousel ? PRODUCT_POOL_QUICK_VIEW_TAGGING : PROS_QUICK_VIEW_TAGGING);

    productQVStore.toggle({
      productId: String(productId),
      swatchColor: colorId,
      slideoutTitle,
      navigationContext: {},
    });
  },
});

onMounted(async () => {
  loadTagLibrary(brand, tagMngrEnv, false);
  await ExperimentationTags.fireAdobeTags();
  if (isRoktSDKPlusEnabled) {
    import('@nuxt-common/site-monetization/app/composables/useRoktSDKPlus')
      .then(({ default: useRoktSDKPlus }) => {
        useRoktSDKPlus({ brand, isRoktSDKPlusEnabled, screenName: 'plp' });
      })
      .catch((err) => {
        console.warn('[Rokt] Failed to load SDK+:', err);
      });
  }
});
</script>

<template>
  <div>
    <NuxtPage page-key="static" />
    <DiscoveryModalOverlay />
  </div>
</template>
